import { useState } from 'react';
import { Activity, Sparkles, SlidersHorizontal } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { apiGet } from '@/api/client';
import type { Department } from '@/api/types';
import { Alert } from '@/app/components/ui/Alert';
import { Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { visibleDepartments } from '@/features/me/permissions';
import type { DashboardRange } from '@/features/statistics/api';
import { runDashboardBatch } from '@/features/statistics/api';
import { RangeSelect } from '@/features/statistics/RangeSelect';
import { getParticipation } from '@/features/participation/api';
import {
  listLocations,
  vitalityPanels,
  type Location,
  type VitalityTarget,
} from '@/features/vitality/api';
import { deriveCards } from '@/features/vitality/deriveCards';
import { buildInsightText, periodPhrase } from '@/features/vitality/insight';
import { deriveDailySeries } from '@/features/vitality/series';
import { VitalityCard } from '@/features/vitality/VitalityCard';
import { CARD_IDS } from '@/features/vitality/metrics';
import { useAsync } from '@/lib/useAsync';
import i18n from '@/i18n';

/** "Alle deelnemers"/"Leidinggevenden"/"Medewerkers" — `undefined` (all)
 * sends no `role` at all; the other two map straight onto `VitalityTarget`'s
 * own `role`. Same filter `OverviewPage.tsx`'s `TrendsSection` already
 * implements (`ParticipantFilter` there) — kept as a local copy here since
 * there's no shared module for it today. */
type ParticipantFilter = 'all' | 'manager' | 'employee';

const ALL_LOCATIONS = 'all';

/** The same explicit `{from, to}` `RangeSelect`'s own "Laatste 90 dagen"
 * option computes (kept as a private one-liner here, same as
 * `OverviewPage.tsx`'s own copy — a plain function export from
 * `RangeSelect.tsx` would trip `react-refresh/only-export-components`, since
 * that file otherwise only exports the `RangeSelect` component). Used only
 * to seed this page's default period — `RangeSelect` recomputes the same
 * value itself once the dropdown's "Laatste 90 dagen" option is picked. */
function ninetyDaysRange(): { from: string; to: string } {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 89 * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

function withId(
  rows: readonly Department[],
): (Department & { id: string; company_id: string | null })[] {
  return rows.map((d) => ({ ...d, id: d.id ?? '', company_id: d.company_id ?? null }));
}

/**
 * `/vitality` — Insights › Vitality (§ Navigatiemodel, PLAN-FASE2.md WP-02).
 * Always scoped to the active company, like Departments/Users, and always
 * department-scoped (no "just my own numbers" mode) — a caller without a
 * real company (personal/unaffiliated context) has no department to target
 * at all.
 */
export function VitalityPage() {
  const { company } = useActiveCompany();
  const { t } = useTranslation();

  if (company === null) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('vitality.page.title')} />
        <p className="text-ink text-sm">{t('vitality.page.noScope')}</p>
      </section>
    );
  }

  return <CompanyVitality companyId={company.id} />;
}

function CompanyVitality({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const { permissions } = useMe();

  const departmentsState = useAsync(
    () => apiGet('/companies/{companyId}/departments', { params: { companyId } }),
    [companyId],
  );
  const locationsState = useAsync(() => listLocations(companyId), [companyId]);

  const [departmentId, setDepartmentId] = useState<string>('');
  const [locationId, setLocationId] = useState<string>(ALL_LOCATIONS);
  const [participantFilter, setParticipantFilter] = useState<ParticipantFilter>('all');
  const [range, setRange] = useState<DashboardRange>(ninetyDaysRange());

  const departments = departmentsState.data
    ? visibleDepartments(permissions, companyId, withId(departmentsState.data)).filter((d) =>
        permissions.canViewDepartmentStats({ id: d.id, company_id: d.company_id }),
      )
    : [];
  const effectiveDepartmentId = departmentId || departments[0]?.id || '';
  const locations: Location[] = locationsState.data ?? [];

  const target: VitalityTarget | null = effectiveDepartmentId
    ? {
        department_id: effectiveDepartmentId,
        ...(locationId !== ALL_LOCATIONS ? { location_id: locationId } : {}),
        ...(participantFilter !== 'all' ? { role: participantFilter } : {}),
      }
    : null;

  const batch = useAsync(
    () => (target ? runDashboardBatch(vitalityPanels(target, range)) : Promise.resolve(null)),
    [JSON.stringify(target), JSON.stringify(range)],
  );

  // Only used by the AI-insight banner's "bij een deelname van X%" line —
  // participation has no `user_id` target, so this stays department-scoped
  // (the only vitality target participation can also resolve), and is
  // skipped entirely when the caller can't see any department.
  const participation = useAsync(
    () =>
      effectiveDepartmentId
        ? getParticipation({ type: 'department', id: effectiveDepartmentId }, range).catch(
            () => null,
          )
        : Promise.resolve(null),
    [effectiveDepartmentId, JSON.stringify(range)],
  );

  const trend = batch.data?.panels.trend;
  const restingCurrent = batch.data?.panels['resting-current'];
  const restingPrevious = batch.data?.panels['resting-previous'];
  const cards = deriveCards(trend, restingCurrent, restingPrevious);
  const dailySeries = deriveDailySeries(batch.data?.panels.daily);

  const participationPct =
    participation.data?.status === 'ok'
      ? (participation.data.funnel.find((s) => s.key === 'active_30d')?.pct_of_previous ?? null)
      : null;

  const insightText = cards
    ? buildInsightText({
        cards,
        dailySeries,
        participationPct,
        period: periodPhrase(range, t),
        locale: i18n.language,
        t,
      })
    : null;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('vitality.page.title')} />
      <p className="text-ink/70 -mt-2 mb-2 text-[13px]">{t('vitality.page.description')}</p>

      {insightText && (
        <Alert tone="info" className="flex items-start gap-3">
          <Sparkles className="text-mint-deep mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <span>
            <span className="text-ink block font-semibold">{t('vitality.insight.title')}</span>
            <span className="text-ink text-sm">{insightText}</span>
          </span>
        </Alert>
      )}

      <Alert tone="info" className="flex items-start gap-3">
        <Activity className="text-mint-deep mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <span className="text-ink text-sm">
          <Trans i18nKey="vitality.page.disclaimer">
            All figures are <strong>indicative well-being insights</strong> and are{' '}
            <strong>not intended as a medical diagnosis</strong>. Trends are only shown for a
            sufficient number of participants (privacy by design).
          </Trans>
        </span>
      </Alert>

      <div className="flex flex-col gap-3">
        <div className="text-ink flex items-center gap-2 text-sm font-medium">
          <SlidersHorizontal className="size-4" aria-hidden="true" />
          {t('vitality.filters.title')}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <RangeSelect value={range} onChange={setRange} className="mb-0 w-44" />

          <div className="mb-0 flex w-48 flex-col gap-1.5">
            <label htmlFor="vitality-location" className="sr-only">
              {t('vitality.filters.location.label')}
            </label>
            <Select
              id="vitality-location"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value={ALL_LOCATIONS}>{t('vitality.filters.location.all')}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="mb-0 flex w-48 flex-col gap-1.5">
            <label htmlFor="vitality-department" className="sr-only">
              {t('vitality.filters.department.label')}
            </label>
            <Select
              id="vitality-department"
              value={effectiveDepartmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={departments.length === 0}
            >
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="mb-0 flex w-48 flex-col gap-1.5">
            <label htmlFor="vitality-participants" className="sr-only">
              {t('overview.trends.filters.participants.label')}
            </label>
            <Select
              id="vitality-participants"
              value={participantFilter}
              onChange={(e) => setParticipantFilter(e.target.value as ParticipantFilter)}
            >
              <option value="all">{t('overview.trends.filters.participants.all')}</option>
              <option value="manager">{t('overview.trends.filters.participants.manager')}</option>
              <option value="employee">{t('overview.trends.filters.participants.employee')}</option>
            </Select>
          </div>
        </div>
      </div>

      {departments.length === 0 ? (
        <Alert tone="info">{t('vitality.page.noDepartments')}</Alert>
      ) : batch.error ? (
        <Alert tone="danger">
          {t('statistics.status.loadFailed', { message: batch.error.message })}
        </Alert>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CARD_IDS.map((cardId) => (
            <VitalityCard
              key={cardId}
              cardId={cardId}
              status={cards ? cards[cardId].status : 'insufficient_data'}
              data={batch.loading || !cards ? undefined : cards[cardId]}
              series={dailySeries?.[cardId]}
              departmentId={effectiveDepartmentId}
            />
          ))}
        </div>
      )}
    </section>
  );
}
